import React, { useCallback, useState, useEffect } from 'react'
import { Loader } from '@refera/ui-web'
import Theme from '@refera/ui-core'
import * as Modal from '_components/modal/generic-modal'
import { InfoCircle } from '@refera/ui-icons'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import {
  rateProvider,
  getProviderRates,
  updateProviderRate,
  getProvidersList,
} from '_/modules/provider/actions'
import { userSelector } from '_/modules/authentication/selectors'
import { ControlledRating } from '_/views/manage-providers/components/RatingComponent'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'

import useStyles from './styles'

export function RateProviderModal({ open, setOpen, viewMode, data }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()
  const user = useSelector(userSelector)
  const [isLoading, setIsLoading] = useState(false)
  const [rateData, setRateData] = useState([])
  const { providerId, isGeneralRate } = data || {}

  const methods = useForm({
    mode: 'all',
  })

  const {
    formState: { isDirty, isValid },
  } = methods

  const handleCancel = () => {
    if (isDirty) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          setOpen(false)
        },
        onCancel: closeDialog,
      })
    } else {
      setOpen(false)
    }
  }

  const onSubmit = useCallback(
    values => {
      setIsLoading(true)
      const payload = {
        ...values,
        provider: data?.providerId,
      }
      const action = rateData?.id ? updateProviderRate(rateData.id, payload) : rateProvider(payload)
      dispatch(action)
        .then(() => {
          dispatch(getProvidersList({ mainTradesman: true }))
          showToast({
            type: 'success',
          })
          setOpen(false)
        })
        .catch(() => {
          showToast({
            type: 'error',
          })
        })
        .finally(() => setIsLoading(false))
    },
    [dispatch, showToast, data, rateData]
  )

  useEffect(() => {
    if (rateData) {
      methods.reset(
        {
          budgetDue: rateData?.budgetDue || 0,
          budgetQuality: rateData?.budgetQuality || 0,
          executionDue: rateData?.executionDue || 0,
          executionQuality: rateData?.executionQuality || 0,
          budgetPrice: rateData?.budgetPrice || 0,
        },
        { keepDirty: false, keepIsValid: false }
      )
    }
  }, [rateData])

  useEffect(() => {
    if (!providerId || !open) return

    const params = isGeneralRate
      ? { provider_id: providerId }
      : { provider_id: providerId, agency_id: user?.agency }
    setIsLoading(true)

    dispatch(getProviderRates(params))
      .then(res => {
        setRateData(res)
      })
      .finally(() => setIsLoading(false))
  }, [open, isGeneralRate, user?.agency?.id, providerId])

  return (
    <>
      <Loader hasBackdrop open={isLoading} />
      <Modal.Root className={styles.root} open={open} onClose={() => setOpen(false)}>
        <Modal.TitleModal title="Avaliação do prestador" />

        <Modal.Content className={styles.dialogContent}>
          <FormProvider {...methods}>
            <form
              id="rate-provider-form"
              className={styles.form}
              onSubmit={methods.handleSubmit(onSubmit)}
            >
              <div className={styles.ratingContainer}>
                <ControlledRating
                  name="budgetDue"
                  label="Prazo de orçamento"
                  disabled={viewMode}
                  required
                />
                <ControlledRating
                  name="budgetPrice"
                  label="Preço de orçamento"
                  disabled={viewMode}
                  required
                />
                <ControlledRating
                  name="budgetQuality"
                  label="Qualidade de orçamento"
                  disabled={viewMode}
                  required
                />

                <ControlledRating
                  name="executionDue"
                  label="Prazo de execução"
                  disabled={viewMode}
                  required
                />
                <ControlledRating
                  name="executionQuality"
                  label="Qualidade de execução"
                  disabled={viewMode}
                  required
                />
              </div>
              <div className={styles.infoText}>
                <InfoCircle color={Theme.Colors.Primary.Base} fontSize={12} />
                <Modal.Text style={{ textAlign: 'left', lineHeight: '2.5rem', fontSize: '10px' }}>
                  Considere avaliação quando houver de 1 a 5 estrelas. Se o prestador tiver zero
                  estrelas, quer dizer que ainda não foi avaliado.
                </Modal.Text>
              </div>
            </form>
          </FormProvider>
        </Modal.Content>

        <Modal.Actions>
          {!viewMode && (
            <>
              <Modal.ButtonRed onClick={handleCancel}>Cancelar</Modal.ButtonRed>
              <Modal.ButtonFullBlue
                form="rate-provider-form"
                type="submit"
                disabled={isLoading || !isValid}
              >
                Salvar
              </Modal.ButtonFullBlue>
            </>
          )}
          {viewMode && (
            <Modal.ButtonFullBlue onClick={() => setOpen(false)}>Fechar</Modal.ButtonFullBlue>
          )}
        </Modal.Actions>
      </Modal.Root>
    </>
  )
}
